import { useSearchParams } from 'react-router';
import { CircleAlert, LogIn } from 'lucide-react';
import { useMeta } from '../api/queries';
import { RobotMark } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { Haptic } from '../kit/haptics';

/** Only same-origin relative paths are accepted as post-login destinations. */
export function safeRedirect(r: string | null): string {
  return r && r.startsWith('/') && !r.startsWith('//') && !r.startsWith('/\\') ? r : '/';
}

export default function LoginPage() {
  const [params] = useSearchParams();
  const redirect = safeRedirect(params.get('redirect'));
  const error = params.get('error');
  const meta = useMeta();

  const signIn = () => window.location.assign(`/auth/login?redirect=${encodeURIComponent(redirect)}`);

  return (
    <main className="relative flex min-h-dvh flex-col bg-canvas dr-aurora dr-safe-px-4 dr-safe-pt-4 dr-safe-pb-6">
      <div className="flex justify-end">
        <ThemeToggle />
      </div>
      <div className="flex flex-1 items-center justify-center py-8">
        <div className="card w-full max-w-md animate-rise dr-surface">
          <div className="card-body items-center gap-5 p-6 text-center sm:p-8">
            <RobotMark className="size-14" />
            <div className="space-y-2">
              <h1 className="text-[1.75rem] leading-tight font-bold tracking-tight">
                Sign in to <span className="dr-dusk-text">DevRobot</span>
              </h1>
              <p className="text-sm text-base-content/65">DevRobot uses your company GitLab account. Which projects you can see and operate follows your GitLab membership and role.</p>
            </div>
            {error ? (
              <div role="alert" className="alert w-full animate-pop items-start alert-soft alert-error text-left">
                <CircleAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-semibold">Sign-in did not complete</p>
                  <p className="break-anywhere text-sm">{error}</p>
                  <p className="mt-1 text-sm">Try again. If GitLab denies access, ask a group owner to check your membership.</p>
                </div>
              </div>
            ) : null}
            <Haptic className="w-full">
              <button type="button" className="btn btn-block btn-lg btn-primary" onClick={signIn}>
                <LogIn className="size-5" aria-hidden="true" />
                Sign in with GitLab
              </button>
            </Haptic>
            {meta.data ? (
              <p className="text-xs text-base-content/55">
                GitLab <span className="font-mono">{meta.data.gitlab.url}</span> · group <span className="font-mono">{meta.data.gitlab.group}</span>
                {meta.data.gitlab.mode === 'simulator' ? <span className="badge badge-warning badge-soft badge-xs ml-2">simulator</span> : null}
              </p>
            ) : null}
            {redirect !== '/' ? <p className="text-xs text-base-content/55">You will return to {redirect} after signing in.</p> : null}
          </div>
        </div>
      </div>
    </main>
  );
}
